/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './BreadcrumbNavigation';
import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { helpRenders } from './BreadcrumbNavigation_Help';

const BreadcrumbNavigationManager: ComponentManagerFn<
  'BreadcrumbNavigation'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'BreadcrumbNavigation'>(
      platform,
      'BreadcrumbNavigation',
    ),
    type: 'BreadcrumbNavigation',
    name: 'Breadcrumb Navigation',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'TriggerOnClick',
          'TriggerOnCreate',
          'TriggerOnInitialize',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      comboDatasourceAlias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Select Options',
        allowedTypes: [
          'CloudDataStore',
          'ReadOnlyDataStore',
          'LocalDataStore',
          'DataStore',
        ],
        required: true,
      },
      labelAttribute: {
        aliasType: 'LOV',
        getAttributeDataTypes: () => ['String'],
        label: 'Label Attribute',
        category: 'Select Options',
        required: true,
        type: 'ComboAttributeChooser',
      },
      idAttribute: {
        label: 'ID Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        category: 'Select Options',
        required: true,
        getAttributeDataTypes: () => ['String', 'Integer', 'UserID', 'Decimal'],
      },
      parentIdAttribute: {
        label: 'Parent ID Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        category: 'Select Options',
        required: true,
        getAttributeDataTypes: () => ['String', 'Integer', 'UserID', 'Decimal'],
      },
      doNotPrefetch: {
        label: 'Do Not Prefetch',
        type: 'boolean',
        category: 'Select Options',
      },
      initialSelection: {
        label: 'Initial Selection',
        type: 'select',
        options: toOptions(['deep', 'first', 'none', 'deepButLast']),
        category: 'Select Options',
        helpText: `Choose \`Deep\` to select the first root option and all the way down to it's first leaf option. Choose \`First\` to only select the first root option without selecting any of the child options.
          \`Deep But Last\` is similar to \`Deep\` except that it will leave the last level unselected.`,
      },
      maxVisibleLevels: {
        label: 'Max Visible Level Expression',
        type: 'expressionOnly',
        returnType: () => 'number',
        category: 'Select Options',
      },
      placeholder: {
        label: 'Placeholder Expression',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Select Options',
      },
      autoSelectFirstChild: {
        label: 'Auto Select First Child Expression',
        type: 'booleanExpression',
        category: 'Select Options',
      },
      onClickFunction: {
        label: 'On Click',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      updateAllowed: {
        label: 'Update Allowed',
        type: 'booleanExpression',
        category: 'Select Options',
      },
      margin: {
        label: 'Margin',
        type: 'margin',
        category: 'Spacing',
      },
      padding: {
        label: 'Padding',
        type: 'padding',
        category: 'Spacing',
      },
      maxWidth: {
        label: 'Max Width',
        category: 'Spacing',
        type: 'string',
      },
    },
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'labelAttribute':
        case 'idAttribute':
        case 'parentIdAttribute':
        case 'doNotPrefetch':
        case 'autoSelectFirstChild':
          return !!itemData.comboDatasourceAlias;
        default:
          return true;
      }
    },
    isDataSourceAliasUsed: (item, alias) =>
      item.props.comboDatasourceAlias === alias,
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'comboDatasourceAlias' } : undefined,
    category: 'Dashboard',
    icon: { iconName: 'list-alt' },
    description: `**Overview**<br/>
The Breadcrumb Navigation component creates a dynamic, multi-level path interface that allows users to navigate hierarchical data structures. Unlike a standard "static" breadcrumb that simply shows where you are, this component is fully interactive—users can click any segment of the path to jump back to a parent level or switch branches in the hierarchy.
It is best used for deep organizational structures, such as Product Categories (Electronics > Laptops > Gaming), Organizational Charts (Company > Department > Team), or File Systems.

**1. Data Configuration (The Foundation)**<br/>
This component is entirely data-driven. It requires a specific relational structure to build the hierarchy automatically.
* **Data Store Alias:** Select the Data Store (e.g., Cloud, Local, or ReadOnly) containing your hierarchical records.
* **Label Attribute:** The text field to display for each segment (e.g., "Category Name").
* **ID Attribute:** The unique identifier for each record (e.g., "CategoryID").
* **Parent ID Attribute:** The field that points to the parent record (e.g., "ParentCategoryID").
* **How it works:** The component uses the Parent ID to recursively link items together. Records with no Parent ID (or null) become the "Root" (first level).

**2. Selection Behavior**<br/>
Control how the breadcrumb initializes and reacts when the page loads.
* **Initial Selection:** Determines the default state of the path.
    * **None:** Starts empty; the user sees nothing selected.
    * **First:** Selects only the first root item.
    * **Deep:** Automatically selects the first root, then its first child, and so on, all the way to the last leaf node.
    * **Deep But Last:** Selects the entire path down to the leaf but leaves the final item unselected.
* **Auto Select First Child:** A boolean expression. If True, whenever a user selects a parent folder, the system automatically selects the first available child inside it.
* **Do Not Prefetch:** Optimization setting. If enabled, the component will not load child levels until the user explicitly requests them. Use this for very large datasets to improve initial load performance.

**3. Visual Control**<br/>
Manage how the path is displayed to ensure it fits within your UI constraints.
* **Max Visible Level:** An expression that limits the number of breadcrumb segments shown. If the path is deeper than this limit, earlier levels may be collapsed.
* **Placeholder:** Text displayed when no selection has been made (e.g., "Select a Category...").
* **Max Width:** Restricts the horizontal space the component can occupy.

**4. Interaction & Logic**<br/>
* **On Click:** A controller function triggered whenever a user clicks a segment. Use this to filter a dashboard, navigate to a new page, or update a secondary data store based on the selection.
* **Update Allowed:** A boolean expression that dynamically enables or disables user interaction (e.g., making the breadcrumb read-only based on user permissions).
* **Triggers:** You can attach logic triggers to handle lifecycle events (TriggerOnClick, TriggerOnInitialize).`,
    helpRenders,
  };
};

export default BreadcrumbNavigationManager;
